import React, { useState } from 'react';

import { AxiosError } from 'axios';
import { ApplicationRoutes, queryParamsRanges } from 'common/constants';
import Jsme, { molFile, setMolFile, smiles as JSMESmiles } from 'components/Jsme';
import ResultSelection from 'features/searchMolecules/ResultSelection';
import {
  getIsQueryLoading,
  searchMoleculesThunk,
} from 'features/searchMolecules/searchMoleculesSlice';
import QueryParametersInputs from 'features/searchMolecules/SearchView/QueryParameters';
import { getSelectedCalculations } from 'features/settings/settingsSlice';
import {
  changeWorkingQueryMolFile,
  changeWorkingQueryParam,
  getWorkingQuery,
  resetWorkingQueryParams,
} from 'features/workingQuery/workingQuerySlice';
import { useAuth, useThunkDispatch } from 'hooks';
import clamp from 'lodash/clamp';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, CircularProgress, Container, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Examples from './Examples';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    searchContainer: {
      display: 'grid',
      gridTemplateColumns: '0 repeat(2, 1fr)',
      gridColumnGap: theme.spacing(4),
      gridRowGap: theme.spacing(2),
      gridAutoRows: 'auto',
      margin: theme.spacing(3, 0),
      marginTop: theme.spacing(10),
    },
    searchIndicator: {
      position: 'absolute',
    },
  }),
);

const SearchView = () => {
  const { t } = useTranslation(['common', 'search']);
  const classes = useStyles();
  const history = useHistory();

  const dispatch = useThunkDispatch();
  const isLoading = useSelector(getIsQueryLoading);
  const { isAuthenticated } = useAuth();
  const workingQuery = useSelector(getWorkingQuery);
  const selectedCalcs = useSelector(getSelectedCalculations);

  const [error, setError] = useState<string | null>(null);

  const getAxiosResponseError = (message: string, status: number): string => {
    if (status === 404) {
      return t('search:no_results_404');
    } else if (status === 500) {
      return t('search:no_results_500');
    } else {
      return t('search:generic_error');
    }
  };

  const handleMoleculeSearch = () => {
    setError(null);
    dispatch(
      searchMoleculesThunk(
        JSMESmiles(),
        workingQuery.queryParams,
        selectedCalcs.map((calc) => calc.id),
        molFile(),
      ),
    )
      .then((payload) => {
        if (payload.result.groupCount > 0) {
          history.push(ApplicationRoutes.Results);
        }
        dispatch(changeWorkingQueryMolFile({ molFile: payload.molFile, shouldJSMEUpdate: true }));
      })
      .catch((error: AxiosError) => {
        console.error({ error });
        if (!!error.response) {
          setError(getAxiosResponseError(error.message, error.response.status));
        } else {
          setError(t('no_server'));
        }
      });
  };

  const isQueryParamValid = (name: string, value: string) => {
    if (name === 'hops') {
      return true;
    }
    if (value === '') {
      if (name === 'groupLimit') {
        return true;
      }
      return false;
    } else {
      if (
        Number(value) > queryParamsRanges[name][0] &&
        Number(value) < queryParamsRanges[name][1]
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const handleQueryParamsChange = (name: string) => (event: any) => {
    if (isQueryParamValid(name, event.target.value)) {
      let value: number | null;
      if (name === 'groupLimit' && event.target.value === '') {
        value = null;
      } else {
        value = clamp(Number(event.target.value), ...queryParamsRanges[name]);
      }
      dispatch(changeWorkingQueryParam({ name, value }));
    }
  };

  const handleJSMEChange = (event: any): void => {
    dispatch(changeWorkingQueryMolFile({ molFile: event.src.molFile(), shouldJSMEUpdate: false }));
  };

  return (
    <>
      <ResultSelection />
      <Container maxWidth="md">
        <section className={classes.searchContainer}>
          <Jsme
            width="400px"
            height="370px"
            options={{ options: 'nozoom,newlook' }}
            molFile={workingQuery.molFile || ''}
            callbacks={[{ name: 'AfterStructureModified', func: handleJSMEChange }]}
            shouldJSMEUpdate={workingQuery.shouldJSMEUpdate}
          />
          <div>
            <QueryParametersInputs
              queryParams={workingQuery.queryParams}
              handleQueryParamsChange={handleQueryParamsChange}
              resetQueryParameters={() => dispatch(resetWorkingQueryParams)}
            />
            <Button
              disabled={!isAuthenticated || isLoading}
              variant="contained"
              color="primary"
              onClick={() => handleMoleculeSearch()}
            >
              {t('search:search')}
              {isLoading && <CircularProgress size={24} className={classes.searchIndicator} />}
            </Button>
            {error && <Typography variant="h6">{error}</Typography>}
          </div>
          <div />
          <div>
            <Typography align="left" variant="h6">
              {t('search:start_with_example')}
            </Typography>
            <Examples setJSME={setMolFile} />
          </div>
        </section>
      </Container>
    </>
  );
};

export default SearchView;
